import React, { useState, useEffect } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import ShareDocumentDialog from './ShareDocumentDialog';
import { formatTimeLeft } from '../../utils/time';
import './PermissionDashboard.css';

interface Permission {
  documentId: string;
  viewerId: string;
  viewerEmail: string;
  viewerName?: string;
  canView: boolean;
  canDownload: boolean;
  canPrint: boolean;
  watermark: boolean;
  allowedPages?: number[];
  deniedPages?: number[];
  grantedAt: string;
  grantedBy: string;
  expiresAt?: string | null;
}

interface Document {
  documentId: string;
  name: string;
  pageCount: number;
  createdAt: string;
}

interface PermissionDashboardProps {
  documentId?: string;
  className?: string;
}

const PermissionDashboard: React.FC<PermissionDashboardProps> = ({
  documentId,
  className = ''
}) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [updatingPermission, setUpdatingPermission] = useState<string | null>(null);
  const [resharingId, setResharingId] = useState<string | null>(null);
  const [reshareSuccess, setReshareSuccess] = useState<string | null>(null);
  const [reshareError, setReshareError] = useState<string | null>(null);

  const { permissions: permissionsApi, documents: documentsApi } = useApi();
  const { planLimits } = useAuth();
  const shareLimit = planLimits.maxSharesPerDocument;
  const atShareLimit = permissions.length >= shareLimit;

  // Transform API response format to frontend interface format
  const transformDocument = (apiDoc: any): Document => ({
    documentId: apiDoc.document_id,
    name: apiDoc.name,
    pageCount: apiDoc.page_count,
    createdAt: apiDoc.created_at
  });

  const transformPermission = (apiPermission: any, knownDocumentId?: string): Permission => ({
    documentId: apiPermission.document_id || knownDocumentId || '',
    viewerId: apiPermission.viewer_id,
    viewerEmail: apiPermission.viewer_email,
    viewerName: apiPermission.viewer_name,
    canView: apiPermission.can_view,
    canDownload: apiPermission.can_download,
    canPrint: apiPermission.can_print,
    watermark: apiPermission.watermark ?? false,
    allowedPages: apiPermission.allowed_pages,
    deniedPages: apiPermission.denied_pages,
    grantedAt: apiPermission.granted_at,
    grantedBy: apiPermission.granted_by,
    expiresAt: apiPermission.expires_at ?? null,
  });

  useEffect(() => {
    loadData();
  }, [documentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      if (documentId) {
        // Load permissions for specific document
        const [permissionsData, documentData] = await Promise.all([
          permissionsApi.getDocumentPermissions(documentId),
          documentsApi.getDocument(documentId)
        ]);
        
        const transformedPermissions = (permissionsData.permissions || []).map((p: any) => transformPermission(p, documentId));
        const transformedDocument = transformDocument(documentData);
        
        setPermissions(transformedPermissions);
        setSelectedDocument(transformedDocument);
        setDocuments([transformedDocument]);
      } else {
        // Load all documents for permission overview
        const documentsData = await documentsApi.getDocuments();
        const transformedDocuments = (documentsData.documents || []).map(transformDocument);
        setDocuments(transformedDocuments);
        
        if (transformedDocuments.length > 0) {
          setSelectedDocument(transformedDocuments[0]);
          const permissionsData = await permissionsApi.getDocumentPermissions(transformedDocuments[0].documentId);
          const transformedPermissions = (permissionsData.permissions || []).map((p: any) => transformPermission(p, transformedDocuments[0].documentId));
          setPermissions(transformedPermissions);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissionsForDocument = async (document: Document) => {
    try {
      setSelectedDocument(document);
      setLoading(true);
      
      const permissionsData = await permissionsApi.getDocumentPermissions(document.documentId);
      const transformedPermissions = (permissionsData.permissions || []).map((p: any) => transformPermission(p, document.documentId));
      setPermissions(transformedPermissions);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionUpdate = async (
    permission: Permission,
    field: 'canView' | 'canDownload' | 'canPrint',
    value: boolean
  ) => {
    try {
      setUpdatingPermission(`${permission.documentId}-${permission.viewerId}-${field}`);
      
      const updatedPermissions = {
        canView: permission.canView,
        canDownload: permission.canDownload,
        canPrint: permission.canPrint,
        watermark: permission.watermark,
        [field]: value,
        allowedPages: permission.allowedPages,
        deniedPages: permission.deniedPages
      };

      await permissionsApi.updatePermissions(
        permission.documentId,
        permission.viewerId,
        updatedPermissions
      );

      // Update local state
      setPermissions(prev => prev.map(p => 
        p.documentId === permission.documentId && p.viewerId === permission.viewerId
          ? { ...p, [field]: value }
          : p
      ));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update permission');
    } finally {
      setUpdatingPermission(null);
    }
  };

  const handleRevokeAccess = async (permission: Permission) => {
    if (!window.confirm(`Stop sharing with ${permission.viewerEmail || permission.viewerName || permission.viewerId}? They will immediately lose access even if they have the document link.`)) {
      return;
    }

    try {
      setUpdatingPermission(`${permission.documentId}-${permission.viewerId}-revoke`);
      
      await permissionsApi.revokeAccess(permission.documentId, permission.viewerId);
      
      // Remove from local state
      setPermissions(prev => prev.filter(p => 
        !(p.documentId === permission.documentId && p.viewerId === permission.viewerId)
      ));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke access');
    } finally {
      setUpdatingPermission(null);
    }
  };

  const handleShareSuccess = () => {
    setShowShareDialog(false);
    if (selectedDocument) {
      loadPermissionsForDocument(selectedDocument);
    }
  };

  const handleReshare = async (permission: Permission) => {
    const key = `${permission.documentId}-${permission.viewerId}`;
    setResharingId(key);
    setReshareSuccess(null);
    setReshareError(null);
    try {
      await permissionsApi.shareDocument(permission.documentId, permission.viewerId, {
        canView: permission.canView,
        canDownload: permission.canDownload,
        canPrint: permission.canPrint,
        watermark: permission.watermark,
        allowedPages: permission.allowedPages,
        deniedPages: permission.deniedPages,
      });
      setReshareSuccess(key);
      setTimeout(() => setReshareSuccess(null), 3000);
    } catch {
      setReshareError(key);
      setTimeout(() => setReshareError(null), 3000);
    } finally {
      setResharingId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPageRestrictions = (permission: Permission): string => {
    if (permission.allowedPages && permission.allowedPages.length > 0) {
      return `Pages: ${permission.allowedPages.join(', ')}`;
    }
    if (permission.deniedPages && permission.deniedPages.length > 0) {
      return `Blocked: ${permission.deniedPages.join(', ')}`;
    }
    return 'All pages';
  };

  const getPermissionSummary = (permission: Permission): string => {
    const perms = [];
    if (permission.canView) perms.push('View');
    if (permission.canDownload) perms.push('Download');
    if (permission.canPrint) perms.push('Print');
    return perms.join(', ') || 'No permissions';
  };

  if (loading && permissions.length === 0) {
    return <LoadingSpinner message="Loading permissions..." />;
  }

  return (
    <div className={`permission-dashboard ${className}`}>
      {error && (
        <ErrorMessage 
          message={error} 
          onDismiss={() => setError('')} 
        />
      )}

      <div className="dashboard-header">
        <div className="header-content">
          <h4>Permission Management</h4>
          <p className="text-muted">
            Manage document sharing and access permissions
          </p>
        </div>
        
        {selectedDocument && (
          <button
            className="btn btn-primary share-btn-with-badge"
            onClick={() => setShowShareDialog(true)}
            disabled={atShareLimit}
            title={atShareLimit ? `Share limit reached (${shareLimit}). Remove a share to add another.` : undefined}
          >
            Share Document
            <span className="share-count-badge">{permissions.length}/{shareLimit}</span>
          </button>
        )}
      </div>

      {/* Document Selector */}
      {!documentId && documents.length > 1 && (
        <div className="document-selector mb-4">
          <label htmlFor="documentSelect" className="form-label">
            Select Document
          </label>
          <select
            id="documentSelect"
            className="form-select"
            value={selectedDocument?.documentId || ''}
            onChange={(e) => {
              const doc = documents.find(d => d.documentId === e.target.value);
              if (doc) loadPermissionsForDocument(doc);
            }}
          >
            {documents.map((doc) => (
              <option key={doc.documentId} value={doc.documentId}>
                📄 {doc.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Current Document Info */}
      {selectedDocument && (
        <div className="current-document-info">
          <div className="document-card">
            <div className="document-icon">📄</div>
            <div className="document-details">
              <h6>{selectedDocument.name}</h6>
              <div className="document-meta">
                <span>{selectedDocument.pageCount} pages</span>
                <span>•</span>
                <span>Created {formatDate(selectedDocument.createdAt)}</span>
                <span>•</span>
                <span>{permissions.length} shared with</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Matrix */}
      {permissions.length === 0 ? (
        <div className="empty-permissions">
          <div className="empty-icon">🔒</div>
          <h6>No Permissions Set</h6>
          <p className="text-muted">
            This document hasn't been shared with anyone yet.
            Click "Share Document" to grant access to users.
          </p>
        </div>
      ) : (
        <div className="permissions-matrix">
          <div className="matrix-header">
            <h6>Current Permissions ({permissions.length})</h6>
          </div>
          
          <div className="permissions-table">
            <div className="table-header">
              <div className="col-user">User</div>
              <div className="col-permissions">Permissions</div>
              <div className="col-pages">Page Access</div>
              <div className="col-granted">Granted</div>
              <div className="col-actions">Actions</div>
            </div>
            
            {permissions.map((permission) => (
              <div key={`${permission.documentId}-${permission.viewerId}`} className="table-row">
                <div className="col-user">
                  <div className="user-info">
                    <div className="user-avatar">
                      {(permission.viewerName || permission.viewerEmail || permission.viewerId || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="user-details">
                      <div className="user-name">
                        {permission.viewerName || permission.viewerEmail || permission.viewerId}
                      </div>
                      {(permission.viewerName || permission.viewerEmail) && permission.viewerEmail !== permission.viewerId && (
                        <div className="user-email">{permission.viewerEmail}</div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="col-permissions">
                  <div className="permission-toggles">
                    <div className="permission-toggle">
                      <input
                        type="checkbox"
                        id={`view-${permission.viewerId}`}
                        checked={permission.canView}
                        onChange={(e) => handlePermissionUpdate(permission, 'canView', e.target.checked)}
                        disabled={updatingPermission === `${permission.documentId}-${permission.viewerId}-canView`}
                      />
                      <label htmlFor={`view-${permission.viewerId}`}>View</label>
                    </div>
                    <div className="permission-toggle">
                      <input
                        type="checkbox"
                        id={`download-${permission.viewerId}`}
                        checked={permission.canDownload}
                        onChange={(e) => handlePermissionUpdate(permission, 'canDownload', e.target.checked)}
                        disabled={updatingPermission === `${permission.documentId}-${permission.viewerId}-canDownload`}
                      />
                      <label htmlFor={`download-${permission.viewerId}`}>Download</label>
                    </div>
                    <div className="permission-toggle">
                      <input
                        type="checkbox"
                        id={`print-${permission.viewerId}`}
                        checked={permission.canPrint}
                        onChange={(e) => handlePermissionUpdate(permission, 'canPrint', e.target.checked)}
                        disabled={updatingPermission === `${permission.documentId}-${permission.viewerId}-canPrint`}
                      />
                      <label htmlFor={`print-${permission.viewerId}`}>Print</label>
                    </div>
                  </div>
                </div>
                
                <div className="col-pages">
                  <span className="page-restrictions">
                    {formatPageRestrictions(permission)}
                  </span>
                </div>
                
                <div className="col-granted">
                  <span className="granted-date">
                    {formatDate(permission.grantedAt)}
                  </span>
                  {(() => {
                    const timeLeft = formatTimeLeft(permission.expiresAt);
                    if (!timeLeft) return null;
                    return (
                      <span className={`expiry-badge${timeLeft === 'Expired' ? ' expiry-expired' : ''}`}>
                        {timeLeft}
                      </span>
                    );
                  })()}
                </div>
                
                <div className="col-actions">
                  {(() => {
                    const key = `${permission.documentId}-${permission.viewerId}`;
                    return (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {reshareSuccess === key ? (
                          <span className="reshare-feedback reshare-ok">✓ Sent</span>
                        ) : reshareError === key ? (
                          <span className="reshare-feedback reshare-err">✗ Failed</span>
                        ) : (
                          <button
                            className="btn-reshare"
                            disabled={resharingId === key}
                            onClick={() => handleReshare(permission)}
                            title="Re-send share notification email"
                          >
                            {resharingId === key ? <LoadingSpinner size="small" inline /> : '↩ Re-Share'}
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRevokeAccess(permission)}
                          disabled={updatingPermission === `${key}-revoke`}
                          title="Revoke access"
                        >
                          {updatingPermission === `${key}-revoke` ? (
                            <LoadingSpinner size="small" inline />
                          ) : (
                            'Stop Sharing'
                          )}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {selectedDocument && (
        <ShareDocumentDialog
          documentId={selectedDocument.documentId}
          documentName={selectedDocument.name}
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          onShareSuccess={handleShareSuccess}
        />
      )}
    </div>
  );
};

export default PermissionDashboard;